import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiFetch from "../../utlis/apiFetch";
import { forceLogout } from "../../utlis/forceLogout";
import moment from "moment";

const handleAuthError = (error, rejectWithValue) => {
  if (error.message === "Your session has expired") forceLogout();
  return rejectWithValue(error.message);
};

export const fetchDashboardData = createAsyncThunk(
  "dashboard/fetchDashboardData",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const { fromMonth, fromYear, toMonth, toYear, countries } = params;

      // Build URL with optional date filters
      let apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/application/dashboard`;
      const queryParams = [];

      // Add fromDate if provided
      if (fromMonth && fromYear) {
        const fromDate = `${fromYear}-${String(fromMonth).padStart(2, '0')}-01`;
        queryParams.push(`fromDate=${fromDate}`);
      }

      // Add toDate if provided
      if (toMonth && toYear) {
        const toDate = moment(`${toYear}-${toMonth}`, 'YYYY-M').endOf('month').format('YYYY-MM-DD');
        queryParams.push(`toDate=${toDate}`);
      }

      // Add country params if provided
      if (countries && countries.length > 0) {
        countries.forEach((country) => {
          queryParams.push(`countries=${encodeURIComponent(country)}`);
        });
      }

      if (queryParams.length > 0) {
        apiUrl += '?' + queryParams.join('&');
      }

      const response = await apiFetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response._data ?? await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch dashboard data");
      }


      return {
        data: data.body,
        dateParams: { fromMonth, fromYear, toMonth, toYear },
      };
    } catch (err) {
      return handleAuthError(err, rejectWithValue);
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    dashboardData: null,
    isLoading: false,
    error: null,
    countries: [],
    fromMonth: null,
    fromYear: null,
    toMonth: null,
    toYear: null,
  },
  reducers: {
    clearDashboardData: (state) => {
      state.dashboardData = null;
    },
    setCountries: (state, action) => {
      state.countries = action.payload;
    },

    setDateFilters: (state, action) => {
      const { fromMonth, fromYear, toMonth, toYear } = action.payload;
      state.fromMonth = fromMonth;
      state.fromYear = fromYear;
      state.toMonth = toMonth;
      state.toYear = toYear;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardData = action.payload.data;


        const { fromMonth, fromYear, toMonth, toYear } =
          action.payload.dateParams;
        state.fromMonth = fromMonth;
        state.fromYear = fromYear;
        state.toMonth = toMonth;
        state.toYear = toYear;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearDashboardData, setCountries, setDateFilters } =
  dashboardSlice.actions;
export default dashboardSlice.reducer;
